import wordmarkBlack from '@/assets/TC_Logo_Black.svg'
import wordmarkWhite from '@/assets/TC_Logo_White.svg'

/**
 * Full-page notice shown when the tenant configuration can't be loaded.
 *
 * This doubles as the cutover mechanism. The app fetches {domainId}/config.json from CloudFront
 * on every load, so withholding that one file takes that site — and only that site — offline
 * behind this screen. Restoring the file and invalidating the cache brings it back; open tabs
 * recover on their own via the reload below, without anyone having to tell users to refresh.
 *
 * It renders by replacing document.body, which is why it's plain DOM rather than a component:
 * it runs when tenant config failed, so Vue was never mounted. Styles are inlined for the same
 * reason — the thing that failed may be the CDN.
 *
 * Note this is the screen for *every* startup config failure, not only a withheld file. The
 * wording stays true either way, but an operator diagnosing a real outage should read the
 * console error main.ts logs, not this page.
 */

/** How long a tab waits before re-checking. Short enough that nobody sits on a stale page. */
export const RETRY_INTERVAL_MS = 60_000

export const RETRY_BUTTON_ID = 'tc-maintenance-retry'

function template(wordmark: string): string {
	return `
		<style>
			.tc-maintenance {
				--tc-bg: #ffffff;
				--tc-panel: #f8fafc;
				--tc-border: #e2e8f0;
				--tc-accent: #125a9d;
				--tc-on-accent: #ffffff;
				--tc-heading: #1e293b;
				--tc-text: #475569;
				--tc-muted: #94a3b8;
				position: fixed;
				inset: 0;
				z-index: 99999;
				display: flex;
				flex-direction: column;
				align-items: center;
				justify-content: center;
				gap: 32px;
				padding: 24px;
				overflow: auto;
				background-color: var(--tc-bg);
				color: var(--tc-text);
				font-family: 'Roboto', system-ui, -apple-system, 'Segoe UI', sans-serif;
				line-height: 1.6;
			}

			@media (prefers-color-scheme: dark) {
				.tc-maintenance {
					--tc-bg: #0f172a;
					--tc-panel: #1e293b;
					--tc-border: #334155;
					--tc-accent: #829ab1;
					/* The dark accent is light enough that white text on it falls under 3:1.
					   Flipping to the page background keeps the button readable. */
					--tc-on-accent: #0f172a;
					--tc-heading: #f1f5f9;
					--tc-text: #cbd5e1;
					--tc-muted: #64748b;
				}
			}

			.tc-maintenance__mark {
				width: min(280px, 65vw);
				height: auto;
			}

			.tc-maintenance__card {
				width: 100%;
				max-width: 560px;
				padding: 40px 32px;
				border: 1px solid var(--tc-border);
				border-radius: 8px;
				background-color: var(--tc-panel);
				text-align: center;
			}

			.tc-maintenance__status {
				display: inline-flex;
				align-items: center;
				gap: 10px;
				margin-bottom: 20px;
				font-size: 13px;
				font-weight: 500;
				letter-spacing: 0.08em;
				text-transform: uppercase;
				color: var(--tc-accent);
			}

			.tc-maintenance__dot {
				width: 9px;
				height: 9px;
				border-radius: 50%;
				background-color: var(--tc-accent);
				animation: tc-maintenance-pulse 2s ease-in-out infinite;
			}

			@keyframes tc-maintenance-pulse {
				50% { opacity: 0.25; }
			}

			@media (prefers-reduced-motion: reduce) {
				.tc-maintenance__dot { animation: none; }
			}

			.tc-maintenance__card h1 {
				margin: 0 0 16px;
				font-size: clamp(22px, 5vw, 28px);
				font-weight: 500;
				text-wrap: balance;
				color: var(--tc-heading);
			}

			.tc-maintenance__card p {
				margin: 0 0 16px;
				font-size: 16px;
			}

			/* Qualified by the card on purpose: the generic paragraph rule above is (0,1,1) and
			   would otherwise win, zeroing this margin and dropping the divider onto the
			   button. Don't shorten this back to a single class. */
			.tc-maintenance__card .tc-maintenance__note {
				padding-top: 20px;
				margin: 24px 0 0;
				border-top: 1px solid var(--tc-border);
				font-size: 14px;
				color: var(--tc-muted);
			}

			.tc-maintenance__retry {
				margin: 8px 0 12px;
				padding: 9px 22px;
				border: none;
				border-radius: 4px;
				background: var(--tc-accent);
				color: var(--tc-on-accent);
				font: inherit;
				font-size: 15px;
				cursor: pointer;
			}

			.tc-maintenance__footer {
				font-size: 12px;
				color: var(--tc-muted);
			}
		</style>
		<div class="tc-maintenance">
			<img class="tc-maintenance__mark" src="${wordmark}" alt="TurnCare">
			<main class="tc-maintenance__card" role="status" aria-live="polite">
				<div class="tc-maintenance__status">
					<span class="tc-maintenance__dot" aria-hidden="true"></span>
					<span>Maintenance in progress</span>
				</div>
				<h1>We&rsquo;ll be back shortly</h1>
				<p>The TurnCare Guardian Portal is temporarily unavailable while we complete scheduled maintenance.</p>
				<p>Your data is safe and no action is needed on your part.</p>
				<button type="button" id="${RETRY_BUTTON_ID}" class="tc-maintenance__retry">Try again</button>
				<p class="tc-maintenance__note">This page checks for you automatically, so you can leave it open. If you need immediate assistance, please contact your TurnCare representative.</p>
			</main>
			<div class="tc-maintenance__footer">&copy; TurnCare, INC. All rights reserved.</div>
		</div>
	`
}

/**
 * Replace the page with the maintenance notice and start re-checking.
 *
 * Replacing the body also removes the boot screen from index.html, which lives outside #app and
 * would otherwise keep spinning over the top of this.
 */
export function renderMaintenanceScreen(): void {
	const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false

	document.body.innerHTML = template(prefersDark ? wordmarkWhite : wordmarkBlack)
	document
		.getElementById(RETRY_BUTTON_ID)
		?.addEventListener('click', () => window.location.reload())

	window.setTimeout(() => window.location.reload(), RETRY_INTERVAL_MS)
}
